import {
	AutocompleteResult as SDKAutocompleteResult,
	NewApiResponse as SDKAutocompleteResponse,
} from 'autosuggest-sdk'
import { FORM_TYPE } from '@/app/constants'

export type AutocompleteResult = SDKAutocompleteResult
export type AutocompleteResponse = SDKAutocompleteResponse

export interface Suggestion {
	id: string
	type: SearchItemLocationType
	title: string
	locationName: string
	image?: string
	displayName: string
	placeID: string
	country: string
}

export interface HotelItem {
	title: string
	translatedName?: string
	image: string
	largeImage: string
	link: string
	price: string
	totalRate: number
	// Used for tracking
	currency: string
	rawPrice: number
	lang: string
	//
	bestPriceOn: string
	provider: string
	rating: number
	ratingText?: string
	stars: number
	location: string
	popular?: boolean
	rates: SearchInPlaceHotel['rates']
	distanceFromCenter: number
	propertyType: number
	numberOfReviews: number
	rawHotelData: SearchInPlaceHotel
}

export interface CityItem {
	name: string
	image: string
	rating: number
	url: string
}

export interface Allocation {
	adults: number
	childAge: number[]
}

export interface Room {
	adults: number
	children: number
}

export const DEFAULT_ROOM: Room = {
	adults: 2,
	children: 0,
}

export interface NavItem {
	title: string
	href: string | null
	onClick?: () => void
}

export interface HotelsListResponse {
	hotels: HotelItem[]
	page: number
	pages: number
	totalResults: number
	complete: boolean
	refreshInterval: number
	lowestTotalRate: number
	highestTotalRate: number
	lowestNightlyRate: number
	highestNightlyRate: number
	searchParams: {
		'check-in-date'?: string
		'check-out-date'?: string
		room?: string[]
	}
	filters?: FiltersCountMap
}

export interface QueryPageParam {
	page: number
	offset: number
}

export interface PaginatedHotelsResponse extends HotelsListResponse {
	offset?: number
}
export interface HotelsDataType {
	pages: PaginatedHotelsResponse[]
	pageParams: any[]
}

export interface HotelItemLocalStorage extends HotelItem {
	id: string
	searchParams: HotelsListResponse['searchParams']
	additionalPrice?: boolean
	passthruLink: string
	isSingleHotel?: boolean
}

export interface CitiesListResponse {
	cities: CityItem[]
}

export interface SearchItemImages {
	image_jpeg: string //'https://content.r9cdn.net/rimg/dimg/05/fd/515362c8-lm-3638-165cdb13a57.jpg?width=128&height=128&xhint=2017&yhint=1499&outputtype=JPEG&crop=true'
	image_webp: string //'https://content.r9cdn.net/rimg/dimg/05/fd/515362c8-lm-3638-165cdb13a57.jpg?width=128&height=128&xhint=2017&yhint=1499&outputtype=WEBP&crop=true'
}

export interface SearchItemType {
	type: string //'airport'
	displayName: string //'Airport'
}

export interface SortOption {
	title: string
	shortTitle: string
	by: SORT_BY
	dir?: SORT_DIR
}

export enum SearchItemLocationType {
	COUNTRY = 'ctry',
	CITY = 'city',
	AIRPORT = 'ap',
	HOTEL = 'hotel',
	REGION = 'reg',
	LM = 'lm',
	FREEREGION = 'freereg',
	GOOGLE_PLACE = 'google_place',
	HOOD = 'hood',
	TOURISTREGION = 'touristregion',
}

export enum SearchPlaceType {
	COUNTRY = 'Countries',
	REGION = 'Regions',
	CITY = 'Cities',
	TOWN = 'Towns',
	HOTEL = 'Hotels',
	AIRPORT = 'Airports',
	LANDMARK = 'Landmark',
}

export enum LocationType {
	COUNTRY = 'country',
	REGION = 'region',
	CITY = 'city',
	HOTEL = 'hotel',
	AIRPORT = 'airport',
}

export interface SearchItem {
	id: string // '12345'
	displayname: string //'All airports, Rome, Italy, (ROM)'
	loctype: SearchItemLocationType // ap, city
	country: string //'Italy'
	cc: string //'IT'
	rc: string //'LA'
	hotelname: string
	cityname: string //'Rome, Italy'
	timezone: string | null //'Europe/Rome'
	utc: string //'+01:00'
	airportname: string //'All airports'
	ap: string //'ROM'
	apicode: string //'ROM'
	cityonly: string //'Rome'
	ctid: string // 12345
	destination_images?: SearchItemImages
	displayType: SearchItemType
	indexId: string //'metro-ROM'
	isMetroOnly: boolean
	kayakId: string //'ROM'
	kayakType: string //'ap'
	locationname: string //'Rome, Italy'
	name: string //'All airports'
	placeID: string //'42802'
	ptid: string //'AROM'
	region: string //'Lazio'
	searchFormPrimary: string //'ROM'
	searchFormSecondary: string //'Rome, Italy'
	secondary: string //'Rome, Italy'
	shortdisplayname: string //'Rome, Italy (ROM)'
	smartyDisplay: string //'All airports, Rome, Italy, (ROM)'
	subregion: string //'Rome'
	entityKey: string
	lat: number //41.9027835
	lng: number //12.4963655
	googlePlaceId?: string //'ChIJuY5gZ-V6b4wR5v6iJQQw5w'
	utcOffsetMinutes?: number //-540
}

export interface SearchInPlaceRequestParams {
	allocations?: Allocation[]
	checkIn?: string //'2023-11-16'
	checkOut?: string //'2023-11-16'
	clientIP?: string //'127.0.0.1'
	currency?: string //'USD'
	language?: string //'en'
	page?: number //0
	perPage?: number //10
	timeZone?: string //'America/New_York'
	utcOffsetMinutes?: number //-540
	place: {
		entityKey?: string
		name: string
		type: SearchPlaceType
		excludedHotels?: string[]
	}
	query?: string //'string'
	sessionID?: string //'string'
	userAgent?: string //'string'
	s?: string //stars
	gr?: string | null //guest rating
	h?: string //hotel chain
	p?: string //property
	f?: string //facilities
	t?: string //themes
	mn?: number //min price
	mx?: number //max price
	sort?: {
		by?: SORT_BY
		dir?: SORT_DIR
	}
	userCountry?: string //US
	ap?: string // exp|tu|vr
	onlyComplete?: boolean // true
}

export interface InitParams {
	lng: string //'en',
}

export interface InitAltParams {
	lng: string //'en',
	imid: string //'12345',
	user: string //'bbbbbbbbbbbbbbbbbbbbbbbbbbbbbbbb',
}

export interface SearchInPlaceHotelRate {
	totalRate: string // 1346.80,
	nightlyRate: string // 192.40,
	providerName: string // "Agoda.com",
	bookURI: string // "https://hapi-en-us.kayakaffiliates.com/..."
	hasFreeCancellation: boolean
	canPayLater: boolean
	// Used for traking
	rawPrice: number
	provider: {
		name: string
		logo: string
	}
}
export interface Policy {
	name: string
	description: string
}
export interface SearchInPlaceHotel {
	id: string // '12345'
	key: string // 'khotel:12345'
	description?: string
	features?: number[]
	featureSummary?: Record<string, string[]>
	policies?: Record<string, Policy> //checkin: { name: 'Check-in', description: 'From 3:00 pm' },
	address: string //'string'
	distance: number // 2358 - distance from center in meters
	numberOfReviews: number // 1234
	propertyType: number
	bestRate: {
		availableRooms: number
		bookURI: string
		canPayLater: boolean
		hasFreeCancellation: boolean
		inclusions: [0]
		isCheapestRate: boolean
		providerIndex: number
		roomName: string
		totalRate: number
		nightlyRate: number
		provider: {
			name: string
			logo: string
		}
	}
	guestRating: number // 8.5
	guestRatingSentiment: string //'string'
	image: string //'string'
	largeImage: string //string
	lat?: number // 41.9027835
	long?: number // 12.4963655
	mainImage?: {
		small: string
		large: string
	}
	name: string //'string'
	translatedName?: string //'string'
	starRating: 1 | 2 | 3 | 4 | 5
	rates: SearchInPlaceHotelRate[]
	moreImages?: { small: string; large: string }[]
}
type FilterId = string
type FilterCount = number

export interface FiltersCountMap {
	facilities: Record<FilterId, FilterCount>
	guestRatings: Record<FilterId, FilterCount>
	hotelThemes: Record<FilterId, FilterCount>
	propertyTypes: Record<FilterId, FilterCount>
	starRatings: Record<FilterId, FilterCount>
}

export interface SearchInPlaceRequestResponse {
	countryCode: string //'string'
	currencyCode: string //'string'
	hotels: SearchInPlaceHotel[]
	languageCode: string //'string'
	page: number
	pages: number
	perPage: number
	totalResults: number
	complete: boolean
	pullTimeout: number
	lowestTotalRate: number
	highestTotalRate: number
	lowestNightlyRate: number
	highestNightlyRate: number
	filters?: FiltersCountMap
}

export interface DestPagesProps {
	params: { lang: string; dest: string[] }
	searchParams: {
		page?: string
		entityKey?: string
		'check-in-date'?: string
		'check-out-date'?: string
		room?: string[]
		currency?: string
		loctype: string
		s?: string
		gr?: string
		h?: string //hotel chain
		p?: string //property
		fa?: string //facilities
		t?: string //themes
		mn?: number // price min
		mx?: number // price max
		by?: SORT_BY
		dir?: SORT_DIR
		sessionID?: string
		userCountry?: string
		ap?: string
		slug?: string
		destcode?: string
		locationName?: string
		placeID?: string
		hotelID?: string
		timezone?: string
		b?: string // b=b
		userIP: string
		userAgent?: string
	}
}

export interface CityNames {
	newYork: string
	london: string
	rome: string
	hawaii: string
	toronto: string
	lasVegas: string
	malta: string
	maldives: string
	dublin: string
}

export interface RedirectRequestParams {
	tab?: string
	rooms?: number
	adults?: number
	children?: number
	dta?: number
	los?: number
	city?: string
	currency?: string
	lang?: string
	price?: string
	stars?: number
	'additional-price'?: boolean
	'hotel-name'?: string
	'hotel-country'?: string
	'location-name'?: string
	'destination-key'?: string
	'check-in-date'?: string
	'check-out-date'?: string
	'redirect-provider'?: string
	'redirect-partner'?: string
}

export interface RedirectRequestResponse {
	trackingID: string
}

export interface KayakRedirectResponse {
	url: string | null
	success: boolean
	error?: string
}

export type FormType = keyof typeof FORM_TYPE

export type Currency = {
	label: string // US Dollar
	flag: string // USA
	abbreviation: string // USD
}

export type UserData = {
	userCountry: string // MD
	defaultCurrency: Currency
	selectedCurrency?: Currency
	sessionID?: string
	uid?: string
	email: string
	userIP: string // 127.0.0.1
	userAgent: string // Mozilla/5.0 (Windows NT 10.0; Win64; x64)
}

export type BookingDataLocalStorage = {
	'check-in-date': string
	'check-out-date': string
	city: string
	'country-code': string
	destination: string
	'destination-id': string
	'destination-key': string
	dta: number
	f: string
	'hotel-country': string
	'location-name': string
	los: number
	rooms: number
	'state-code': string
	tab: string
	vert: string
	_adults: number
	_children: number
	_childAges?: number[]
}

export enum SORT_BY {
	CONSUMER_RATING = 'consumerRating',
	DISTANCE = 'distance',
	NAME = 'name',
	MIN_RATE = 'minRate',
	POPULARITY = 'popularity',
	RATING = 'rating',
}

export enum SORT_DIR {
	ASC = 'asc',
	DESC = 'desc',
}

// Stuff for Sanity
export interface InternationalizedStringValue {
	_type: string
	_key: string
	value: string
}

export interface PageDataObject {
	headingTwo: InternationalizedStringValue[]
	metaDescription: InternationalizedStringValue[]
	headingOne?: InternationalizedStringValue[]
	metaTitle: InternationalizedStringValue[]
	slug: string
	themes?: string[]
	facilities?: string[]
	stars: string[]
	properties: string[]
	chains: string[]
	currency: string
}

export interface Filters {
	stars: number[]
	rating: number[]
	priceRange?: [number, number]
	facilities: number[]
	stayOptions: number[]
	hotelStyles?: number[]
}

export interface SanityData {
	metaTitle: string
	metaDescription: string
	headingOne: string
	headingTwo: string
	themes?: string[]
	facilities?: string[]
	stars: string[]
	properties: string[]
	chains: string[]
	currency: string
}
